// src/pages/TrainingResponse/TrainingResponsePage.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TrainingResponseAI } from "../../lib/trainingresponseApi";

function LoaderDot() {
  return <span className="loader-dot" />;
}

export default function TrainingResponsePage() {
  const [chat, setChat] = useState([]);
  const [input, setInput] = useState("");
  const [desc, setDesc] = useState(""); 

  const [customText, setCustomText] = useState("");
  const [scenarioBusy, setScenarioBusy] = useState(false);

  const [busy, setBusy] = useState(false);
  const [emotionCard, setEmotionCard] = useState(null); 
  const [strategyText, setStrategyText] = useState("");
  const [predicted, setPredicted] = useState([]); 
  const [modelAns, setModelAns] = useState(null); 
  const [summary, setSummary] = useState(null); 
  const [report, setReport] = useState(null); 

  const didInit = useRef(false);

  // 초기 시나리오
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    (async () => {
      try {
        const init = await TrainingResponseAI.init();
        setChat([{ role: "ai", text: init.aiMessage.parts?.[0]?.text || "" }]);
        setDesc(init.description || "");
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const hasAtLeastOneUserMsg = useMemo(() => chat.some((m) => m.role === "user"), [chat]);

  const resetAllViews = useCallback(() => {
    setEmotionCard(null);
    setStrategyText("");
    setPredicted([]);
    setModelAns(null);
    setSummary(null);
    setReport(null);
  }, []);

  const handleReset = useCallback(async () => {
    resetAllViews();
    setCustomText("");
    setInput("");
    try {
      const init = await TrainingResponseAI.init();
      setChat([{ role: "ai", text: init.aiMessage.parts?.[0]?.text || "" }]);
      setDesc(init.description || "");
    } catch (e) {
      console.error(e);
    }
  }, [resetAllViews]);

  const handleStartCustomScenario = useCallback(async () => {
    const t = customText.trim();
    if (!t) {
      alert("시나리오를 입력해주세요.");
      return;
    }
    setScenarioBusy(true);
    try {
      const sc = await TrainingResponseAI.customScenario(t);
      resetAllViews();
      setChat([{ role: "ai", text: sc.aiMessage.parts?.[0]?.text || "" }]);
      setDesc(sc.description || "");
      setInput("");
    } catch (e) {
      console.error(e);
      alert("시나리오 생성에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setScenarioBusy(false);
    }
  }, [customText, resetAllViews]);

  const handleSend = useCallback(async () => {
    const t = input.trim();
    if (!t || busy) return;

    resetAllViews();
    setBusy(true);
    try {
      const nextChat = [...chat, { role: "user", text: t }];
      setChat(nextChat);

      // 감정 분석 + 코칭
      try {
        const emo = await TrainingResponseAI.emotionCoaching(t);
        setEmotionCard(emo); 
      } catch (e) {
        console.error("emotionCoaching", e);
      }

      // 다음 응답 + 전략
      try {
        const { response, strategy } = await TrainingResponseAI.reply(nextChat);
        setChat((prev) => [...prev, { role: "ai", text: response }]);
        setStrategyText(strategy || "");
      } catch (e) {
        console.error("reply", e);
        setChat((prev) => [...prev, { role: "ai", text: "죄송합니다, 시스템 오류가 발생했습니다. 잠시 후 다시 시도해주세요." }]);
      }
    } finally {
      setBusy(false);
      setInput("");
    }
  }, [input, chat, busy, resetAllViews]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handlePredict = useCallback(async () => {
    if (!hasAtLeastOneUserMsg) return;
    try {
      const firstUser = chat.find((m) => m.role === "user")?.text || "";
      const arr = await TrainingResponseAI.predictQuestions(firstUser);
      setPredicted(Array.isArray(arr) ? arr : []);
    } catch (e) {
      console.error(e);
      setPredicted([]);
      alert("예상 질문을 불러오는 데 실패했습니다.");
    }
  }, [chat, hasAtLeastOneUserMsg]);

  const handleModelAnswer = useCallback(async () => {
    if (!hasAtLeastOneUserMsg) return;
    try {
      const r = await TrainingResponseAI.modelAnswer(chat);
      setModelAns(r);
    } catch (e) {
      console.error(e);
      setModelAns(null);
      alert("모범 답변을 생성하는 데 실패했습니다.");
    }
  }, [chat, hasAtLeastOneUserMsg]);

  const handleSummarize = useCallback(async () => {
    if (!hasAtLeastOneUserMsg) return;
    try {
      const r = await TrainingResponseAI.summarize(chat);
      setSummary(r);
    } catch (e) {
      console.error(e);
      setSummary(null);
      alert("대화 분석에 실패했습니다.");
    }
  }, [chat, hasAtLeastOneUserMsg]);

  const handleReport = useCallback(async () => {
    if (!hasAtLeastOneUserMsg) return;
    try {
      const r = await TrainingResponseAI.report(chat);
      setReport(r);
    } catch (e) {
      console.error(e);
      setReport(null);
      alert("보고서 생성에 실패했습니다.");
    }
  }, [chat, hasAtLeastOneUserMsg]);

  return (
    <section>
      <div id="content_title">
        <h2>AI 불만 고객 응대 트레이닝 (리버스)</h2>
        <p>이제 사용자가 불만 고객이 되어, AI 직원의 응대를 훈련합니다.</p>
      </div>

      <div className="content">
        {/* 1) 시나리오 선택 */}
        <div className="mrdef">
          <h3 className="sectionTitle">1. 시나리오 선택</h3>
          <div>
            <p>기본 시나리오로 시작하거나, 나만의 시나리오를 입력하세요.</p>
            <div className="chatInput mrdef">
              <input placeholder="예: 예약 시간보다 너무 오래 기다렸어요." value={customText} onChange={(e) => setCustomText(e.target.value)} />
              <button className="btn" onClick={handleStartCustomScenario} disabled={scenarioBusy}>
                {scenarioBusy ? "생성중.." : "나만의 시나리오로 시작"}
              </button>
            </div>
            {desc && <p>{desc}</p>}
          </div>
        </div>

        {/* 2) 시뮬레이션 */}
        <div className="mrdef ">
          <h3 className="sectionTitle">2. AI 직원 응대 시뮬레이션</h3>

          {/* 채팅 로그 */}
          <div className="chatbox">
            <div className="chatlog">
              {chat.map((m, i) => (
                <div key={i} className={`bubble ${m.role === "user" ? "from-user" : "from-patient"}`}>
                  <div className="text">{m.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* 입력 */}
        <div className="chatInput ">
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="불편한 증상을 입력해보세요…" />
          <button className="btn" onClick={handleSend} disabled={busy}>
            {busy ? (
              <>
                보내는 중 <LoaderDot />
              </>
            ) : (
              "불만 제기"
            )}
          </button>
        </div>
        {/* 감정 분석 + 코칭 */}
        {emotionCard && (
          <div className="mrdef">
            <h4 className="sim-title">실시간 감정 분석 및 코칭</h4>
            <div>
              <div>
                <div>
                  <strong>감지된 감정</strong>
                  <div>{emotionCard.emotion}</div>
                </div>
                <div className="border-l pl-4">
                  <strong>대응 코칭</strong>
                  <div>{emotionCard.coachingTip}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 전략 설명 */}
        {strategyText && (
          <div className="mrdef">
            <h4 className="sim-title">AI 응대 전략</h4>
            <div>{strategyText}</div>
          </div>
        )}

        {/* 액션 버튼들 */}
        <div className="mrdef">
          <div className="trfResponse_bottomBtn">
            <button className="btn" onClick={handlePredict} disabled={!hasAtLeastOneUserMsg}>
              AI 예상 질문
            </button>
            <button className="btn" onClick={handleModelAnswer} disabled={!hasAtLeastOneUserMsg}>
              모범 답변 보기
            </button>
            <button className="btn" onClick={handleSummarize} disabled={!hasAtLeastOneUserMsg}>
              대화 요약/평가
            </button>
            <button className="btn" onClick={handleReport} disabled={!hasAtLeastOneUserMsg}>
              응대 보고서 생성
            </button>
            <button className="reset btn" onClick={handleReset}>
              전체 초기화
            </button>
          </div>
        </div>

        {/* 예상 질문 */}
        {predicted.length > 0 && (
          <div className="mrdef">
            <h4 className="sim-title">AI의 예상 질문</h4>
            <div>
              <ul className="list-disc ">
                {predicted.map((q, i) => (
                  <li key={i}>{q}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* 모범 답변 */}
        {modelAns && (
          <div className="mrdef">
            <h4 className="sim-title">모범 답변 보기</h4>
            <div>
              <div>
                <strong>모범 답변</strong>
              </div>
              <div>“{modelAns.modelResponse}”</div>
              <div>
                <strong>효과적인 이유</strong>
              </div>
              <div>{modelAns.rationale}</div>
            </div>
          </div>
        )}

        {/* 요약/평가 */}
        {summary && (
          <div className="mrdef">
            <h4 className="sim-title">대화 요약 및 평가</h4>
            <div className=" ">
              <div>
                <strong>대화 요약</strong>
                <p>{summary.summary}</p>
              </div>
              <div>
                <strong>응대 평가</strong>
                <p>👍 잘한 점: {summary.evaluation?.strength}</p>
                <p>💡 개선할 점: {summary.evaluation?.improvement}</p>
              </div>
              <div>
                <strong>추천 행동 (Best Practice)</strong>
                <p>{summary.recommendation}</p>
              </div>
            </div>
          </div>
        )}

        {/* 보고서 */}
        {report && (
          <div className="mrdef">
            <h4 className="sim-title">고객 응대 보고서</h4>
            <div>
              {/* 언어별 키 처리(가이드에 따라) */}
              <div>
                <strong>처리일자/处理日期</strong> : {report["처리일자"] || report["处理日期"] || "-"}
              </div>
              <div>
                <strong>환자명/患者姓名</strong> : {report["환자명"] || report["患者姓名"] || "환자분"}
              </div>
              <div>
                <strong>주요내용/主要内容</strong> : {report["주요내용"] || report["主要内容"] || "-"}
              </div>
              <div>
                <strong>진행된조치/采取措施</strong> : {report["진행된조치"] || report["采取措施"] || "-"}
              </div>
              <div>
                <strong>해결방안/解决方案</strong> : {report["해결방안"] || report["解决方案"] || "-"}
              </div>
              <div>
                <strong>향후조치필요사항/后续必要事项</strong> : {report["향후조치필요사항"] || report["后续必要事项"] || "-"}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
